import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@material-ui/core';
import * as actions from '../../../store/index';
import { Controller, useForm } from 'react-hook-form';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Logo from '../../../resources/coorporate_images/brayz_logo_login.png';
import { fetchPost } from '../../../helpers/fetching';
import MediaLinks from '../common/MediaLinks';
import NeuroConnector from '../../../resources/coorporate_images/neuro_network_small.png';
import { CopyrightShort } from '../common/CopyrightShort';
import { useStyles } from '../Auth.style';
import { SHOW_LOGOS } from '../../../config';
import UE from '../../../resources/external_logos/UE.png';
import ministerioSalute from '../../../resources/external_logos/ministerioSalute.png';
import italiaDomani from '../../../resources/external_logos/italiaDomani.png';
import asstCrema from '../../../resources/external_logos/asstCrema.png';

export default function LoginForm(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const darkState = useSelector((store) => store.gui.darkState);
  const dispatch = useDispatch();
  const showLogos = SHOW_LOGOS;

  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const getApiErrorMessage = (msg) => {
    if (!msg) return t('api_messages.undefined');
    return t(`api_messages.${msg}`, { defaultValue: msg });
  };
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
      disclaimer: false,
    },
  });

  const disclaimer = watch('disclaimer');

  const onSubmit = async (data) => {
    setLoginError('');
    try {
      const response = await fetchPost('login', {
        login: data.username,
        password: data.password,
      });
      if (!response?.success) {
        setLoginError(getApiErrorMessage(response?.msg));
        return;
      }

      localStorage.setItem('username', response.username);
      localStorage.setItem('role', response.role);
      localStorage.setItem('annotation', response.annotation);
      localStorage.setItem('diagnostics', response.diagnostics);
      localStorage.setItem('aliases', response.aliases);
      localStorage.setItem('last_login', response.last_login);

      const redirectUrl = localStorage.getItem('redirect_url');
      if (redirectUrl) {
        localStorage.removeItem('redirect_url');
        props.history.push(redirectUrl);
      } else {
        localStorage.setItem('username', response.username);
        localStorage.setItem('role', response.role);
        localStorage.setItem('annotation', response.annotation);
        localStorage.setItem('diagnostics', response.diagnostics);
        localStorage.setItem('aliases', response.aliases);
        localStorage.setItem('last_login', response.last_login);

        const redirectUrl = localStorage.getItem('redirect_url');

        if (redirectUrl) {
          localStorage.removeItem('redirect_url');
          props.history.push(redirectUrl);
        } else {
          props.history.push('/bbox/results/details');
          dispatch(
            actions.setSnackbar({
              msg: 'api_messages.login_success',
              severity: 'success',
              msgParams: { username: response.username },
            }),
          );
        }
      }
    } catch (e) {
      setLoginError(getApiErrorMessage(e.message));
    }
  };

  const handlePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Grid container component='main' className={classes.root}>
      <Grid
        container
        direction='column'
        item
        xs={6}
        component={Paper}
        elevation={0}
        square
        alignItems='center'
        justifyContent='space-between'
        style={{
          zIndex: '1000',
          backgroundColor: theme.palette.background.mainDark,
        }}>
        <img className={classes.avatar_login} src={Logo} alt='logo missing' />

        <form onSubmit={handleSubmit(onSubmit)} noValidate className={classes.form_login} autoComplete='off'>
          <Controller
            name='username'
            control={control}
            rules={{ required: t('LoginRegister.all_required') }}
            render={({ field }) => (
              <TextField
                variant='outlined'
                {...field}
                className={classes.text_field_login}
                label={t('LoginRegister.username')}
                margin='normal'
                required
                error={!!errors.username}
                helperText={errors.username?.message}
                autoFocus
                data-test-id='login-username'
                inputProps={{
                  autoComplete: 'username',
                  form: { autoComplete: 'off' },
                }}
              />
            )}
          />

          <Controller
            name='password'
            control={control}
            rules={{ required: t('LoginRegister.all_required') }}
            render={({ field }) => (
              <TextField
                variant='outlined'
                {...field}
                className={classes.text_field_login}
                type={showPassword ? 'text' : 'password'}
                label={t('LoginRegister.password')}
                margin='normal'
                required
                error={!!errors.password}
                helperText={errors.password?.message}
                data-test-id='login-password'
                inputProps={{
                  autoComplete: 'current-password',
                  form: { autoComplete: 'off' },
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handlePasswordVisibility}
                      edge='end'
                      aria-label='toggle password visibility'>
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            )}
          />

          <Controller
            name='disclaimer'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                className={classes.disclaimer_text}
                control={<Checkbox {...field} checked={field.value} color='primary' />}
                label={
                  <Typography style={{ fontSize: theme.fonts?.responsiveSmall || '0.75rem' }}>
                    {t('LoginRegister.disclaimer')}
                  </Typography>
                }
                style={{ color: darkState ? '#dfdfdf' : 'gray' }}
              />
            )}
          />

          <div style={{ minHeight: '30px', width: '80%', textAlign: 'center' }}>
            {loginError && (
              <Typography color='error' style={{ fontSize: theme.fonts?.responsiveSmall || '0.75rem' }}>
                {loginError}
              </Typography>
            )}
          </div>

          <Button
            type='submit'
            variant='contained'
            color='primary'
            className={classes.submit_login}
            disabled={!disclaimer || isSubmitting}
            size='medium'
            data-test-id='login-button'>
            {t('LoginRegister.login')}
          </Button>
        </form>

        <Typography variant='body2' color='textSecondary' align='center' className={classes.loginInfo}>
          {t('LoginRegister.first_time')}
          <RouterLink className={classes.register} to='/register' data-test-id='go-to-register-button'>
            {t('LoginRegister.register')}
          </RouterLink>
        </Typography>

        {showLogos && (
          <div className={classes.logosContainer}>
            <img src={UE} alt='UE' className={classes.logo} />
            <img src={ministerioSalute} alt='ministerioSalute' className={classes.logo} />
            <img src={italiaDomani} alt='italiaDomani' className={classes.logo} />
            <img src={asstCrema} alt='asstCrema' className={classes.logo} />
          </div>
        )}

        <Grid
          className={classes.mediaLinks}
          item
          container
          direction='column'
          alignItems='center'
          wrap='nowrap'
          justifyContent='center'
          spacing={1}>
          <Grid item>
            <CopyrightShort />
          </Grid>
          <Grid item>
            <MediaLinks />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        item
        xs={6}
        style={{
          display: 'flex',
          background: 'linear-gradient(270deg, rgb(81 78 78) 0%, rgb(66, 66, 66) 100%)',
        }}>
        <img className={classes.connector_image} src={NeuroConnector} alt='b_rayz connector' />
      </Grid>
    </Grid>
  );
}
